import { apiClient } from '@/api/client';
import {
  USERS,
  userByIdPath,
  userConfirmPermanentPath,
  userCompensationPath,
  userEmploymentPath,
  userRecoverPath,
  userResetPasswordPath,
  userRolePath,
  userStatusPath,
  userTerminatePath,
} from '@/api/endpoints';
import { USERS_API_MAX_LIMIT } from '@/lib/constants';
import type {
  AssignRolePayload,
  ChangeStatusPayload,
  ConfirmPermanentPayload,
  CreateUserPayload,
  PaginatedResponse,
  TerminateUserPayload,
  UpdateCompensationPayload,
  UpdateEmploymentPayload,
  UpdateUserPayload,
  User,
  UserListItem,
  UserListQueryParams,
} from '@/types';
import type { AdminResetPasswordResponse, ApiResponseWrapper } from '@/types/api.types';

/** API may return roleId as string or populated object. */
function normalizeRoleId(roleId: string | { _id: string; name?: string } | undefined): {
  id: string;
  name?: string;
} {
  if (!roleId) return { id: '' };
  if (typeof roleId === 'string') return { id: roleId };
  return { id: roleId._id, name: roleId.name };
}

function toListItem(raw: {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  employeeId: string;
  roleId: string | { _id: string; name?: string };
  status: string;
  isDeleted?: boolean;
  employment?: Record<string, unknown>;
  company?: { id: string; name: string } | null;
  companies?: Array<{ id: string; name: string }>;
}): UserListItem {
  const name = [raw.firstName, raw.lastName].filter(Boolean).join(' ').trim() || raw.email;
  const role = normalizeRoleId(raw.roleId);
  return {
    _id: raw._id,
    id: raw._id,
    email: raw.email,
    name,
    firstName: raw.firstName ?? '',
    lastName: raw.lastName ?? '',
    employeeId: raw.employeeId,
    roleId: role.id,
    roleName: role.name,
    status: (raw.isDeleted ? 'deleted' : raw.status) as UserListItem['status'],
    isDeleted: raw.isDeleted,
    employment: raw.employment,
    company: raw.company ?? null,
    companies: raw.companies ?? (raw.company ? [raw.company] : []),
  };
}

function clampUserListParams(params?: UserListQueryParams): UserListQueryParams | undefined {
  if (!params) return undefined;
  const { companyIds, ...rest } = params;
  const safeParams: UserListQueryParams = { ...rest };
  if (companyIds && companyIds.length > 0) {
    safeParams.companyIds = Array.from(new Set(companyIds)).sort();
  }
  if (safeParams.limit == null) return safeParams;
  return {
    ...safeParams,
    limit: Math.min(Math.max(1, safeParams.limit), USERS_API_MAX_LIMIT),
  };
}

function toUsersRequestParams(params?: UserListQueryParams): Record<string, unknown> | undefined {
  if (!params) return undefined;
  const { companyIds, ...rest } = params;
  const requestParams: Record<string, unknown> = { ...rest };
  if (companyIds && companyIds.length > 0) {
    requestParams.companyIds = companyIds.join(',');
  }
  return requestParams;
}

function toPaginatedList(
  inner: {
    data: Array<{
      _id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      employeeId: string;
      roleId: string | { _id: string; name?: string };
      status: string;
      isDeleted?: boolean;
      employment?: Record<string, unknown>;
      company?: { id: string; name: string } | null;
      companies?: Array<{ id: string; name: string }>;
    }>;
    total: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  },
  params?: UserListQueryParams
): PaginatedResponse<UserListItem> {
  const total = Number(inner.total) || 0;
  const limit = Number(inner.limit ?? params?.limit) || 10;
  const page = Number(inner.page ?? params?.page) || 1;
  const totalPages =
    inner.totalPages != null && inner.totalPages > 0
      ? inner.totalPages
      : Math.ceil(total / limit) || 0;
  const data = Array.isArray(inner.data) ? inner.data.map(toListItem) : [];
  return {
    data,
    total,
    page,
    limit,
    totalPages,
  };
}

export async function createUser(payload: CreateUserPayload): Promise<User> {
  const { data } = await apiClient.post<ApiResponseWrapper<User> | User>(USERS, payload);
  const resolved = data as ApiResponseWrapper<User>;
  if (resolved?.data && typeof resolved.data === 'object' && resolved.data._id) {
    return resolved.data;
  }
  return data as User;
}

export async function getUsers(
  params?: UserListQueryParams
): Promise<PaginatedResponse<UserListItem>> {
  const safeParams = clampUserListParams(params);
  const { data } = await apiClient.get<
    | ApiResponseWrapper<{
        data: Array<{
          _id: string;
          email: string;
          firstName?: string;
          lastName?: string;
          employeeId: string;
          roleId: string | { _id: string; name?: string };
          status: string;
          employment?: Record<string, unknown>;
        }>;
        total: number;
        page: number;
        limit: number;
        totalPages?: number;
      }>
    | PaginatedResponse<UserListItem>
  >(USERS, { params: toUsersRequestParams(safeParams) });

  const wrapped = data as ApiResponseWrapper<{
    data: Array<{
      _id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      employeeId: string;
      roleId: string;
      status: string;
      isDeleted?: boolean;
      employment?: Record<string, unknown>;
      company?: { id: string; name: string } | null;
      companies?: Array<{ id: string; name: string }>;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages?: number;
  }>;
  if (wrapped?.data && typeof wrapped.data === 'object' && Array.isArray(wrapped.data.data)) {
    return toPaginatedList(wrapped.data, safeParams);
  }

  const flat = data as PaginatedResponse<
    UserListItem & { roleId?: string | { _id: string; name?: string } }
  >;
  if (flat && Array.isArray(flat.data) && typeof flat.total === 'number') {
    const list = flat.data.map(
      (item: UserListItem & { _id?: string; roleId?: string | { _id: string; name?: string } }) =>
        toListItem({
          _id: item._id ?? item.id,
          email: item.email,
          firstName: item.firstName,
          lastName: item.lastName,
          employeeId: item.employeeId,
          roleId: typeof item.roleId === 'object' ? item.roleId : (item.roleId ?? ''),
          status: item.status,
          isDeleted: item.isDeleted,
          employment: item.employment,
          company: item.company,
          companies: item.companies,
        })
    );
    return {
      data: list,
      total: flat.total,
      page: flat.page ?? 1,
      limit: flat.limit ?? 10,
      totalPages: flat.totalPages ?? (Math.ceil(flat.total / (flat.limit ?? 10)) || 0),
    };
  }

  return toPaginatedList(
    {
      data: [],
      total: 0,
      page: safeParams?.page ?? 1,
      limit: safeParams?.limit ?? 10,
    },
    safeParams
  );
}

/** Raw user from API; roleId may be populated object. */
interface UserApiRow {
  _id: string;
  organizationId: string;
  email: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  roleId: string | { _id: string; name?: string };
  status: string;
  [key: string]: unknown;
}

function normalizeUser(raw: UserApiRow): User {
  const roleId =
    typeof raw.roleId === 'object' && raw.roleId !== null ? raw.roleId._id : raw.roleId;
  const roleName =
    typeof raw.roleId === 'object' && raw.roleId !== null && typeof raw.roleId.name === 'string'
      ? raw.roleId.name
      : undefined;
  // The populated role is replaced by the ID and name above.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { roleId: _roleId, ...rest } = raw;
  return { ...rest, roleId, roleName } as User;
}

export async function getUserById(id: string): Promise<User> {
  const { data } = await apiClient.get<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userByIdPath(id)
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(userByIdPath(id));
}

export async function recoverUser(id: string): Promise<User> {
  const { data } = await apiClient.put<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userRecoverPath(id),
    {}
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<User> {
  const { data } = await apiClient.put<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userByIdPath(id),
    payload
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function changeUserStatus(id: string, payload: ChangeStatusPayload): Promise<User> {
  const { data } = await apiClient.put<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userStatusPath(id),
    payload
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function confirmUserPermanent(
  id: string,
  payload: ConfirmPermanentPayload
): Promise<User> {
  const { data } = await apiClient.post<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userConfirmPermanentPath(id),
    payload
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function terminateUser(
  id: string,
  payload: TerminateUserPayload
): Promise<User> {
  const { data } = await apiClient.post<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userTerminatePath(id),
    payload
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function updateUserRole(id: string, payload: AssignRolePayload): Promise<User> {
  const { data } = await apiClient.put<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userRolePath(id),
    payload
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function updateCompensation(
  id: string,
  payload: UpdateCompensationPayload
): Promise<User> {
  const { data } = await apiClient.put<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userCompensationPath(id),
    payload
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function updateEmployment(
  id: string,
  payload: UpdateEmploymentPayload
): Promise<User> {
  const { data } = await apiClient.put<ApiResponseWrapper<UserApiRow> | UserApiRow>(
    userEmploymentPath(id),
    payload
  );
  const wrapped = data as ApiResponseWrapper<UserApiRow>;
  const raw =
    wrapped?.data && typeof wrapped.data === 'object' ? wrapped.data : (data as UserApiRow);
  if (raw && typeof raw === 'object' && raw._id) {
    return normalizeUser(raw);
  }
  throw new Error('User not found');
}

export async function triggerUserPasswordReset(id: string): Promise<AdminResetPasswordResponse> {
  const { data } = await apiClient.post<ApiResponseWrapper<AdminResetPasswordResponse>>(
    userResetPasswordPath(id),
    {}
  );
  return data.data;
}

/** Account access (Phase 0 section 3), separate from company employment. */
export type AccountAccessState = 'invited' | 'active' | 'locked' | 'disabled' | 'archived';

export type AccountAccessCommand =
  | 'cancel-invitation'
  | 'lock'
  | 'unlock'
  | 'disable'
  | 'reenable'
  | 'archive';

export interface AccountAccessHistoryEntry {
  requestId: string;
  command: string;
  from: string;
  to: string;
  reason?: string;
  actorId?: string;
  at: string;
}

export interface AccountAccess {
  userId: string;
  accountAccessState: AccountAccessState;
  status: string;
  history: AccountAccessHistoryEntry[];
}

const accountAccessPath = (id: string): string => `${userByIdPath(id)}/access`;

export async function getAccountAccess(id: string): Promise<AccountAccess> {
  const { data } = await apiClient.get<ApiResponseWrapper<AccountAccess>>(accountAccessPath(id));
  return data.data;
}

export async function runAccountAccessCommand(
  id: string,
  command: AccountAccessCommand,
  input: { requestId: string; reason: string }
): Promise<AccountAccess> {
  const { data } = await apiClient.post<ApiResponseWrapper<AccountAccess>>(
    `${accountAccessPath(id)}/${command}`,
    input
  );
  return data.data;
}
